import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { fetchReleases, fetchUnreadCount, markReleaseViewed } from "@/lib/whatsNewApi";

const SESSION_FLAG = "whatsNewModalShown";

// First-login modal: pops once per session when the user has unread releases.
// Shows the most-recent release. "View later" closes silently. "Show me how"
// marks it read on the server and routes to /whats-new.
export default function WhatsNewModal() {
  const [open, setOpen] = useState(false);
  const [release, setRelease] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_FLAG) === "1") return;
    let cancelled = false;
    (async () => {
      try {
        const { unread } = await fetchUnreadCount();
        if (cancelled || unread <= 0) return;
        const releases = await fetchReleases();
        if (cancelled || !releases?.length) return;
        // Show the most-recent one (already pinned-first, then chrono)
        const latest = releases.find(r => !r.pinned) || releases[0];
        setRelease(latest);
        setOpen(true);
        sessionStorage.setItem(SESSION_FLAG, "1");
      } catch {
        /* silent — never block the app */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const close = () => setOpen(false);

  const goToFullPage = async () => {
    if (release) {
      try { await markReleaseViewed(release.id); } catch {}
      window.dispatchEvent(new Event("whats-new-read"));
    }
    setOpen(false);
    navigate("/whats-new");
  };

  if (!release) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg overflow-hidden p-0" data-testid="whats-new-modal">
        <div className="bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4" />
            <span className="text-[11px] font-bold uppercase tracking-wider">Just shipped</span>
          </div>
          <DialogHeader className="space-y-0 p-0">
            <DialogTitle className="text-white text-xl leading-tight" style={{ fontFamily: "Manrope" }}>
              {release.title}
            </DialogTitle>
          </DialogHeader>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-gray-700 leading-relaxed">{release.body}</p>
          {release.bullets?.length > 0 && (
            <ul className="space-y-1.5">
              {release.bullets.slice(0, 3).map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-cyan-500 mt-1.5 leading-none">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter className="px-6 py-3 border-t border-gray-100 bg-gray-50/50">
          <Button variant="outline" onClick={close} data-testid="whats-new-modal-later">View later</Button>
          <Button onClick={goToFullPage} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white" data-testid="whats-new-modal-cta">
            Show me how
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
